import type { WizardStep } from "@/hooks/useWizardFlow";
import { firstStepAfterFinancial } from "@/hooks/useWizardFlow";
import { FINANCIAL_ROUTES } from "@/utils/financialFlow";
import { getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { getIntakeTaskKey, useIntakeProgressStore, type IntakeFlags } from "@/store/intakeProgress";
import { ensureCaseHistoryId } from "@/utils/caseHistory";

const DEMOGRAPHICS = "/complaint/demographics";

/**
 * Read the current task's device-remembered flags. Uses the SAME key source as the
 * mark-done points (getFacilityKeyForPortal / getPatientKey), so entry and marking agree.
 */
export function getIntakeFlagsFor(taskId: number): IntakeFlags {
  return useIntakeProgressStore.getState().getFlags(getIntakeTaskKey(undefined, undefined, taskId));
}

/**
 * Pure decision: which step to open, given the flags, whether the case-history record is
 * ready, and the wizard steps. Returns the first UNFINISHED step. Falls back to Demographics
 * if the case history isn't ready (so history pages never dead-end).
 */
export function pickResumeStep(
  flags: IntakeFlags,
  caseHistoryReady: boolean,
  wizardSteps: WizardStep[]
): string {
  if (!flags.demographics) return DEMOGRAPHICS;
  if (!caseHistoryReady) return DEMOGRAPHICS;
  if (!flags.commPrefs) return FINANCIAL_ROUTES.commPrefs;
  if (!flags.financial) return FINANCIAL_ROUTES.q1;
  const next = firstStepAfterFinancial(wizardSteps);
  return next && next !== "/" ? next : FINANCIAL_ROUTES.primaryComplaint;
}

function hasStep(steps: WizardStep[], path: string): boolean {
  return steps.some((s) => s.path === path);
}

/**
 * Back targets use the SESSION's step list (which reflects what was removed at entry on
 * resume), NOT the live done-flags — those flip the moment you pass a step in the same
 * session, which would wrongly send a first-pass patient to the dashboard. When there are
 * no steps (legacy flow), fall back to the natural previous step.
 */
export function backFromCommPrefs(steps: WizardStep[]): string {
  if (steps.length === 0 || hasStep(steps, DEMOGRAPHICS)) return DEMOGRAPHICS;
  return "/"; // demographics was completed & removed on resume → dashboard
}

export function backFromFinancialQ1(steps: WizardStep[]): string {
  if (steps.length === 0) return FINANCIAL_ROUTES.commPrefs; // legacy: natural previous
  if (hasStep(steps, FINANCIAL_ROUTES.commPrefs)) return FINANCIAL_ROUTES.commPrefs;
  if (hasStep(steps, DEMOGRAPHICS)) return DEMOGRAPHICS;
  return "/";
}

export interface IntakeEntryParams {
  taskId: number;
  wizardSteps: WizardStep[];
}

/** Where a config-driven intake task should open on entry (first unfinished step). */
export async function resolveIntakeEntryPath({ taskId, wizardSteps }: IntakeEntryParams): Promise<string> {
  const flags = getIntakeFlagsFor(taskId);
  if (!flags.demographics) return DEMOGRAPHICS; // no need to touch the case history yet
  // Skipping Demographics — re-establish the case-history record the history pages need.
  const caseHistoryReady = (await ensureCaseHistoryId(getFacilityKeyForPortal(), getPatientKey())) > 0;
  return pickResumeStep(flags, caseHistoryReady, wizardSteps);
}
